import type { BigIntish } from '@morpho-org/blue-sdk'

import { intervalToDuration } from 'date-fns'

/*
 * Dates render on a UTC server and hydrate in the viewer's timezone, so they are
 * formatted in UTC: formatting local time would change the day near midnight and
 * make React discard the server HTML (#418).
 */

/**
 * Format timestamp seconds to a UTC date string.
 *
 * @param timestamp - The input bigint, number or null timestamp to be formatted in seconds.
 * @returns A string representing the UTC date in format YYYY-MM-DD.
 *
 * @example
 * formatTimestampToDate(1633776000n) => returns "2021-10-09"
 */
export const formatTimestampToDate = (timestamp: BigIntish | null) => {
  return new Date(Number(timestamp) * 1000).toISOString().slice(0, 10)
}

/**
 * Format duration from seconds to a string representation.
 *
 * @param duration - The input bigint duration in seconds.
 * @returns A string representing the duration in months (M) or days (D).
 *
 * @example
 * formatDuration(2592000n) => returns "1M"
 * formatDuration(86400n) => returns "1D"
 * @internal
 */
export const formatDuration = (duration: bigint | null) => {
  const { months = 0, days = 0 } = intervalToDuration({
    start: 0,
    end: Number(duration ?? 0) * 1000
  })
  return months > 0 ? `${months}M` : `${days}D`
}

const parseTimestamp = (timestamp: string): Date =>
  /^\d+$/.test(timestamp) ? new Date(Number(timestamp) * 1000) : new Date(timestamp)

const shortDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC'
})

export const formatTimestampToShortDate = (timestamp: string): string =>
  shortDateFormatter.format(parseTimestamp(timestamp))

export const formatVaultAgeDays = (creationTimestamp: string): string => {
  const ms = new Date().getTime() - parseTimestamp(creationTimestamp).getTime()
  const days = Math.floor(ms / (1000 * 60 * 60 * 24))
  if (days < 1) return 'Less than a day'
  return `${days} day${days !== 1 ? 's' : ''}`
}
